import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import { RecurringSchedule, RecurringScheduleClientViewModel } from '../../redux/API/ClientAPIHelpers/recurringSchedule';
import { DispatchProperties } from '../../redux/store';
import { getClients } from '../../redux/State/clientSlice/clientSlice';
import { errorNormalizer, getClientDisplayLabel } from '../../shared/Helpers/functions';

export interface ClientSearchOption {
	id: string;
	label: string;
}

const DEBOUNCE_MS = 300;
const INITIAL_PAGE_SIZE = 20;

// The two client sources are identical apart from the date-of-birth field name:
// getScheduledClients returns dateOfBirth, getClients returns dateOfBirthday.
interface ClientSearchSource {
	dateOfBirth?: string;
	dateOfBirthday?: string;
	fullName?: string;
	id: string;
	isElc?: boolean;
}

const toOption = (client: ClientSearchSource): ClientSearchOption => ({
	id: client.id,
	label: getClientDisplayLabel({
		fullName: client.fullName || '',
		dateOfBirthday: client.dateOfBirth ?? client.dateOfBirthday,
		isElc: client.isElc,
	}),
});

// searchAllClients=false (default) lists clients that already have schedules (Schedules page filter).
// searchAllClients=true lists ALL accessible clients (Create New Schedule modal), so a client without
// any schedule yet can still be selected. Both sources are role/branch scoped server-side.
export const useClientSearch = (includeEnded = false, searchAllClients = false) => {
	const navigate = useNavigate();
	const dispatch = useDispatch<DispatchProperties>();
	const [clientSearch, setClientSearch] = useState('');
	const [clientOptions, setClientOptions] = useState<ClientSearchOption[]>([]);
	const [clientPageSize, setClientPageSize] = useState(INITIAL_PAGE_SIZE);
	const [clientHasMore, setClientHasMore] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>();

	const handleSetClientSearch = (value: string) => {
		setClientSearch(value || '');
		setClientPageSize(INITIAL_PAGE_SIZE);
	};

	useEffect(() => {
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			if (searchAllClients) {
				dispatch(
					getClients({
						page: '1',
						pageSize: String(clientPageSize),
						searchString: clientSearch || '',
					}),
				)
					.then(unwrapResult)
					.then((response) => {
						const list: ClientSearchSource[] = response.query || [];

						setClientOptions(list.map(toOption));
						setClientHasMore(Boolean(response.hasNextPage));
					})
					.catch(errorNormalizer);

				return;
			}

			RecurringSchedule.getScheduledClients({
				page: '1',
				pageSize: String(clientPageSize),
				includeEnded,
				searchString: clientSearch || '',
			})
				.then((response) => {
					if (response?.error?.status === 403) {
						navigate('/dashboard');

						return;
					}

					const list: RecurringScheduleClientViewModel[] = response.query || [];

					setClientOptions(list.map(toOption));
					setClientHasMore(Boolean(response.hasNextPage));
				})
				.catch(errorNormalizer);
		}, DEBOUNCE_MS);

		return () => clearTimeout(debounceRef.current);
	}, [clientPageSize, clientSearch, includeEnded, navigate, dispatch, searchAllClients]);

	return {
		clientHasMore,
		clientOptions,
		clientSearch,
		setClientPageSize,
		setClientSearch: handleSetClientSearch,
	};
};
